import { useTranslation } from 'react-i18next'
import { Base, ReverLoading } from '@itsrever/design-system'
import { SizingModelResult, SizingOverview } from '@/domain/sizing'
import { formatCurrency } from '@/utils'
import { formatPercent } from '@/utils/format'
import { ChangeRow, StatTile } from './primitives'
import { SizingRatesChart } from './charts'

const { Card } = Base

export function OverviewTabLoading() {
  return (
    <Card className="flex h-80 items-center justify-center p-7">
      <ReverLoading />
    </Card>
  )
}

export function OverviewTab({
  overview,
  results
}: {
  overview: SizingOverview
  results: SizingModelResult[]
}) {
  const { t, i18n } = useTranslation()

  if (!overview.cycleDate) {
    return (
      <Base.EmptyState
        variant="card"
        title={t('sizing.models.empty_title')}
        description={t('sizing.models.empty_description')}
        className="h-64"
      />
    )
  }

  const euros = (value: number) =>
    formatCurrency(value.toFixed(0), 'EUR', {
      lang: i18n.language,
      maximumFractionDigits: 0
    })
  const count = (value: number) => value.toLocaleString(i18n.language)
  const lastRate = [...overview.trend]
    .reverse()
    .find((point) => point.ratePct !== null)?.ratePct

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        <StatTile
          hero
          value={euros(overview.savedEurToDate)}
          detail={
            // Redundant when every euro is recent (young banners).
            overview.savedEurLast3m > 0 &&
            overview.savedEurLast3m < overview.savedEurToDate * 0.995
              ? t('sizing.overview.hero_detail', {
                  amount: euros(overview.savedEurLast3m)
                })
              : undefined
          }
          label={t('sizing.overview.hero_label')}
          sub={t('sizing.overview.hero_sub')}
        />
        <StatTile
          value={
            lastRate !== undefined && lastRate !== null
              ? formatPercent(lastRate, i18n.language)
              : '—'
          }
          label={t('sizing.overview.rate_label')}
          sub={t('sizing.overview.rate_sub')}
        />
        <StatTile
          value={count(overview.modelsWithBanner)}
          label={t('sizing.overview.models_label')}
          sub={t('sizing.overview.models_sub', {
            products: count(overview.productsWithBanner),
            newModels: count(overview.newModelsThisCycle)
          })}
        />
        <StatTile
          value={overview.avoidedReturnsMonth.toLocaleString(i18n.language, {
            maximumFractionDigits: 0
          })}
          label={t('sizing.overview.avoided_label')}
          sub={t('sizing.overview.avoided_sub', {
            amount: euros(overview.avoidedEurMonth)
          })}
        />
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Card className="p-4">
          <h3 className="text-base font-semibold">
            {t('sizing.overview.reasons_title')}
          </h3>
          <p className="mb-2 text-xs text-neutral-500">
            {t('sizing.overview.reasons_sub')}
          </p>
          <SizingRatesChart results={results} />
        </Card>

        <Card className="p-4">
          <h3 className="text-base font-semibold">
            {t('sizing.overview.activity_title')}
          </h3>
          <p className="mb-2 text-xs text-neutral-500">
            {t('sizing.overview.activity_sub')}
          </p>
          <ChangeRow
            icon="✓"
            tone="success"
            title={t('sizing.overview.activity_working', {
              n: overview.statusCounts.working
            })}
          >
            {t('sizing.overview.activity_working_desc')}
          </ChangeRow>
          <ChangeRow
            icon="⏳"
            tone="info"
            title={t('sizing.overview.activity_collecting', {
              n: overview.statusCounts.collectingData
            })}
          >
            {t('sizing.overview.activity_collecting_desc')}
          </ChangeRow>
          {overview.statusCounts.rediagnosis > 0 ? (
            <ChangeRow
              icon="🔍"
              tone="warn"
              title={t('sizing.overview.activity_rediagnosis', {
                n: overview.statusCounts.rediagnosis
              })}
            >
              {t('sizing.overview.activity_rediagnosis_desc')}
            </ChangeRow>
          ) : null}
        </Card>
      </div>
    </div>
  )
}
